/**
 * FeaturedProduct.js — Featured Programs carousel section.
 *
 * Renders a Swiper carousel of featured product cards on the home page.
 * Each slide uses ProductGridNew for the card layout.
 * The number of visible slides is responsive (1 / 2 / 4 depending on viewport).
 *
 * Props:
 *  - productData {Array}                  List of featured product objects from the engine API.
 *  - Program_structure_images {Array}     Slug-to-image-URL mapping for card thumbnails.
 *  - AddtoCart {Function}                 Adds a product variant to the cart.
 *  - GAurl {string}                       Google Ads click-ID query string (may be empty).
 *  - program_date_is_gone_or_not {Function} Returns true if a program date has passed.
 *  - is_ten_minite_less {Function}        Returns true if a program starts within 10 minutes.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import { Container } from 'react-bootstrap';

// ─── Internal ─────────────────────────────────────────────────────────────────
import Bg from "../../images/tile-new.png";
import ProductGridNew from '../ProductListing/ProductGrid/ProductGridNew';

// ─── Swiper setup ─────────────────────────────────────────────────────────────
// Modules must be registered before any Swiper instance is created.
SwiperCore.use([Navigation, Pagination]);

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * FeaturedProduct — home-page featured programs carousel.
 * See file-level JSDoc for full props description.
 */
export const FeaturedProduct = (props) => {
  return (
    <div className='featured_wrapper' style={{ backgroundImage: "url(" + Bg + ")" }}>
      <Container>
        <div className='title_wrap text-center'>
          <h2 className='text-primary'>Featured Programs</h2>
        </div>

        <Swiper
          spaceBetween={24}
          slidesPerView={4}
          speed={0}
          navigation={true}
          pagination={{ clickable: true }}
          watchOverflow={true}
          breakpoints={{
            "0":    { slidesPerView: 1 },
            "768":  { slidesPerView: 2 },
            "1200": { slidesPerView: 4 },
          }}
          modules={[Navigation, Pagination]}
          onSwiper={(swiper) => {
            /*
             * Intercept swiper.slideTo — called by arrows, dots and swipe.
             * This lets us run the EXIT animation while old cards are still
             * on-screen, wait for it to finish, then snap to the new index
             * and run the ENTER animation on the incoming cards.
             */
            const orig = swiper.slideTo.bind(swiper);
            let busy = false;

            swiper.slideTo = (index, speed, runCallbacks) => {
              /* If same index or mid-animation, fall through to Swiper */
              if (busy || index === swiper.activeIndex) {
                orig(index, speed, runCallbacks);
                return;
              }
              busy = true;

              const perView    = Math.round(Number(swiper.params.slidesPerView)) || 1;
              const goingNext  = index > swiper.activeIndex;
              const exitAnim   = goingNext ? 'exitLeft'  : 'exitRight';
              const enterAnim  = goingNext ? 'enterRight' : 'enterLeft';
              const exitStart  = swiper.activeIndex;
              const exitEnd    = Math.min(exitStart + perView, swiper.slides.length);

              /* ── 1. Exit: animate current visible cards out ── */
              for (let i = exitStart; i < exitEnd; i++) {
                swiper.slides[i].style.animation = `${exitAnim} 0.28s ease-out forwards`;
              }

              setTimeout(() => {
                /* ── 2. Snap: clear exit, move wrapper instantly ── */
                swiper.slides.forEach(s => { s.style.animation = 'none'; });
                orig(index, 0, runCallbacks);

                /* ── 3. Enter: animate new visible cards in ── */
                const enterStart = swiper.activeIndex;
                const enterEnd   = Math.min(enterStart + perView, swiper.slides.length);
                void swiper.el.offsetWidth; // eslint-disable-line no-void
                for (let i = enterStart; i < enterEnd; i++) {
                  swiper.slides[i].style.animation = `${enterAnim} 0.32s ease-out both`;
                }

                setTimeout(() => { busy = false; }, 320);
              }, 280);
            };
          }}
        >
          {props.productData.map((single_product, index) => {
            // Product data is stored at index 1 of a two-element tuple returned by the API.
            let product_data = Object.values(single_product)[1];
            let product_title = product_data.title;
            let product_slug  = product_data.handle;
            let priceRange    = product_data.priceRange;

            // The full program metadata is stored as a JSON string in the 'all_data' metafield.
            let product_final_data = undefined;
            product_data.metafields.forEach((meta_val) => {
              if (meta_val.key === 'all_data') {
                product_final_data = JSON.parse(meta_val.value);
              }
            });

            return (
              <SwiperSlide key={product_slug || index}>
                <ProductGridNew
                  GAurl={props.GAurl}
                  AddtoCart={props.AddtoCart}
                  AddtoWishlist={props.AddtoWishlist}
                  getAllWishlist={props.getAllWishlist}
                  Program_structure_images={props.Program_structure_images}
                  program_date_is_gone_or_not={props.program_date_is_gone_or_not}
                  is_ten_minite_less={props.is_ten_minite_less}
                  product={single_product[1]}
                  priceRange={priceRange}
                  product_slug={product_slug}
                  product_title={product_title}
                  product_data={product_final_data}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Container>
    </div>
  );
};

export default FeaturedProduct;
